import React, { useState, useEffect } from 'react';
import Slidebar from './Slidebar';
import apiInstance from '../../utils/axios';
import Swal from 'sweetalert2';
import UserData from '../plugin/UserData';
import {
  Settings,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Flag,
  ImagePlus,
  Save,
  Loader2,
  Camera,
  Shield,
  CheckCircle
} from 'lucide-react';

function CustomerSettings() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [initialEmail, setInitialEmail] = useState('');
  const [initialPhone, setInitialPhone] = useState('');

  const fetchProfileData = () => {
    const ud = UserData();
    if (!ud || !ud.user_id) {
      Swal.fire('Error', 'Debes iniciar sesión para ver la configuración.', 'error');
      setLoadingProfile(false);
      return;
    }
    apiInstance.get(`user/profile/${ud.user_id}/`)
      .then((res) => {
        const { full_name, address, city, state, country, user, image } = res.data;
        setFullName(full_name || '');
        setEmail(user?.email || '');
        setPhone(user?.phone || '');
        setAddress(address || '');
        setCity(city || '');
        setState(state || '');
        setCountry(country || '');
        setImagePreview(image || null);
        setInitialEmail(user?.email || '');
        setInitialPhone(user?.phone || '');
        setLoadingProfile(false);
      })
      .catch(() => {
        Swal.fire('Error', 'Error al cargar los datos del perfil', 'error');
        setLoadingProfile(false);
      });
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const formData = new FormData();
    formData.append('full_name', fullName);
    formData.append('address', address);
    formData.append('city', city);
    formData.append('state', state);
    formData.append('country', country);
    if (image) {
      formData.append('image', image);
    }
    if (email !== initialEmail) formData.append('email', email);
    if (phone !== initialPhone) formData.append('phone', phone);

    try {
      const ud = UserData();
      if (!ud || !ud.user_id) throw new Error('No autenticado');
      
      await apiInstance.patch(
        `user/profile/${ud.user_id}/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      Swal.fire({
        title: 'Perfil actualizado exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false
      }).then(() => window.location.reload());
    } catch (error) {
      const res = error.response?.data || {};
      setErrors(res.user || {});
      Swal.fire('Error', res.message || error.message || 'Error en la actualización', 'error');
    }
    setIsLoading(false);
  };

  return (
    <div className="settings-page">
      <div className="settings-container">
        {loadingProfile ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Cargando tu perfil...</p>
          </div>
        ) : (
          <div className="settings-layout">
            <aside className="settings-sidebar">
              <Slidebar />
            </aside>

            <main className="settings-main">
              <form onSubmit={handleSubmit}>
                {/* Profile Photo Section */}
                <div className="settings-card profile-card">
                  <div className="card-header">
                    <h2><Camera size={22} /> Foto de Perfil</h2>
                  </div>
                  <div className="card-body">
                    <div className="photo-upload">
                      <div className="photo-preview">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" />
                        ) : (
                          <div className="photo-placeholder">
                            <User size={48} />
                          </div>
                        )}
                        <label className="upload-overlay">
                          <Camera size={24} />
                          <span>Cambiar</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </label>
                      </div>
                      <div className="photo-info">
                        <h4>Imagen de Perfil</h4>
                        <p>JPG, PNG o GIF. Máximo 5MB.</p>
                        <label className="upload-btn">
                          <ImagePlus size={18} />
                          Subir Imagen
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personal Info Section */}
                <div className="settings-card">
                  <div className="card-header">
                    <h2><User size={22} /> Información Personal</h2>
                  </div>
                  <div className="card-body">
                    <div className="form-grid">
                      <div className="form-group full-width">
                        <label>
                          <User size={16} />
                          Nombre Completo
                        </label>
                        <input
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder="Tu nombre completo"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>
                          <Mail size={16} />
                          Correo Electrónico
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="tu@email.com"
                          className={errors.email ? 'has-error' : ''}
                        />
                        {errors.email && <span className="error-msg">{errors.email}</span>}
                      </div>
                      
                      <div className="form-group">
                        <label>
                          <Phone size={16} />
                          Celular
                        </label>
                        <input
                          type="text"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+1 234 567 890"
                          className={errors.phone ? 'has-error' : ''}
                        />
                        {errors.phone && <span className="error-msg">{errors.phone}</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="settings-card">
                  <div className="card-header">
                    <h2><MapPin size={22} /> Dirección</h2>
                  </div>
                  <div className="card-body">
                    <div className="form-grid">
                      <div className="form-group full-width">
                        <label>
                          <MapPin size={16} />
                          Dirección
                        </label>
                        <input
                          type="text"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          placeholder="Tu dirección"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>
                          <Building size={16} />
                          Ciudad
                        </label>
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="Tu ciudad"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>
                          <Building size={16} />
                          Estado / Provincia
                        </label>
                        <input
                          type="text"
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          placeholder="Tu estado"
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label>
                          <Flag size={16} />
                          País
                        </label>
                        <input
                          type="text"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          placeholder="Tu país"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Security Note */}
                <div className="security-note">
                  <Shield size={20} />
                  <div>
                    <h4>Tu información está segura</h4>
                    <p>Protegemos tus datos con encriptación de última generación.</p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="form-actions">
                  <button type="submit" className="save-btn" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 size={20} className="spinner-icon" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        Guardar Cambios
                      </>
                    )}
                  </button>
                </div>
              </form>
            </main>
          </div>
        )}
      </div>

      <style>{`
        .settings-page {
          background: #ffffff;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        /* Layout */
        .settings-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 32px 24px 60px;
        }

        .settings-layout {
          display: flex;
          gap: 32px;
        }

        .settings-sidebar {
          width: 320px;
          flex-shrink: 0;
        }

        .settings-main {
          flex: 1;
          min-width: 0;
        }

        /* Loading */
        .loading-state {
          text-align: center;
          padding: 80px 20px;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Settings Card */
        .settings-card {
          background: white;
          border-radius: 24px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
          margin-bottom: 24px;
          overflow: hidden;
        }

        .card-header {
          padding: 24px 28px;
          border-bottom: 1px solid #f1f5f9;
        }

        .card-header h2 {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .card-body {
          padding: 28px;
        }

        /* Photo Upload */
        .photo-upload {
          display: flex;
          align-items: center;
          gap: 28px;
        }

        .photo-preview {
          position: relative;
          width: 120px;
          height: 120px;
          border-radius: 24px;
          overflow: hidden;
          flex-shrink: 0;
        }

        .photo-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .photo-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
        }

        .upload-overlay {
          position: absolute;
          inset: 0;
          background: rgba(15, 23, 42, 0.7);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          color: white;
          font-size: 12px;
          font-weight: 600;
          opacity: 0;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }

        .photo-preview:hover .upload-overlay {
          opacity: 1;
        }

        .upload-overlay input {
          display: none;
        }

        .photo-info h4 {
          font-size: 16px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 4px;
        }

        .photo-info p {
          font-size: 13px;
          color: #64748b;
          margin: 0 0 16px;
        }

        .upload-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #f1f5f9;
          color: #0f172a;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .upload-btn:hover {
          background: #e2e8f0;
        }

        .upload-btn input {
          display: none;
        }

        /* Form Grid */
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-group.full-width {
          grid-column: span 2;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-group input {
          width: 100%;
          padding: 14px 16px;
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 15px;
          color: #0f172a;
          transition: all 0.2s ease;
        }

        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          background: white;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        .form-group input::placeholder {
          color: #94a3b8;
        }

        .form-group input.has-error {
          border-color: #ef4444;
        }

        .error-msg {
          display: block;
          font-size: 12px;
          color: #ef4444;
          margin-top: 6px;
        }

        /* Security Note */
        .security-note {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 20px 24px;
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border-radius: 16px;
          margin-bottom: 24px;
        }

        .security-note svg {
          color: #10b981;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .security-note h4 {
          font-size: 14px;
          font-weight: 700;
          color: #065f46;
          margin: 0 0 4px;
        }

        .security-note p {
          font-size: 13px;
          color: #047857;
          margin: 0;
        }

        /* Form Actions */
        .form-actions {
          display: flex;
          justify-content: flex-end;
        }

        .save-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 32px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .save-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.25);
        }

        .save-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner-icon {
          animation: spin 1s linear infinite;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .settings-sidebar {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .settings-container {
            padding: 20px 16px 40px;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-group.full-width {
            grid-column: span 1;
          }

          .photo-upload {
            flex-direction: column;
            text-align: center;
          }

          .save-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default CustomerSettings;